import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAdminApi } from '../hooks/useAdminApi';
import StatsCard from '../components/StatsCard';

const AdminDashboardPage = () => {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const { get } = useAdminApi();
  const [stats, setStats] = useState({ mechs: 0, pilots: 0, mods: 0, skins: 0, players: 0 });
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [mechsRes, pilotsRes, modsRes, skinsRes, playersRes, auditRes] = await Promise.all([
        fetch('/list.json').then(r => r.json()),
        fetch('/pilot_list.json').then(r => r.json()),
        fetch('/mod_list.json').then(r => r.json()),
        fetch('/legendary_skins.json').then(r => r.json()),
        get('/get-all-players.php').catch(() => ({ players: [] })),
        get('/admin-audit-log.php?limit=20').catch(() => ({ entries: [] })),
      ]);
      setStats({
        mechs: mechsRes.filter(m => m.type === 'mech').length,
        weapons: mechsRes.filter(m => m.type === 'weapon').length,
        pilots: pilotsRes.length,
        mods: modsRes.length,
        skins: skinsRes.length,
        players: playersRes?.players?.length || 0,
      });
      setAuditLog(auditRes?.entries || []);
    } catch { /* empty */ } finally { setLoading(false); }
  }, [get]);

  useEffect(() => { loadData(); }, [loadData]);

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loading) return <div className={dark ? 'text-gray-400' : 'text-gray-500'}>Loading...</div>;

  return (
    <div className="space-y-6">
      <h1 className={`text-xl font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatsCard label="Mechs" value={stats.mechs}
          icon="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
        <StatsCard label="Weapons" value={stats.weapons}
          icon="M13 10V3L4 14h7v7l9-11h-7z" />
        <StatsCard label="Pilots" value={stats.pilots}
          icon="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        <StatsCard label="Mods" value={stats.mods}
          icon="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <StatsCard label="Skins" value={stats.skins}
          icon="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        <StatsCard label="Players" value={stats.players}
          icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </div>

      {/* Audit Log */}
      <div className={`rounded-xl border ${dark ? 'bg-[#1A1A1A] border-[#333]' : 'bg-white border-gray-200'}`}>
        <div className={`px-5 py-4 border-b ${dark ? 'border-[#333]' : 'border-gray-200'}`}>
          <h2 className={`text-sm font-semibold ${dark ? 'text-white' : 'text-gray-900'}`}>Recent Activity</h2>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-[#333]">
          {auditLog.length === 0 ? (
            <div className={`px-5 py-8 text-center text-sm ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
              No recent activity
            </div>
          ) : (
            auditLog.map((entry, i) => (
              <div key={i} className="px-5 py-3 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium truncate ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {entry.details}
                  </p>
                  <p className={`text-xs mt-0.5 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {entry.action} &middot; {entry.target}
                  </p>
                </div>
                <span className={`text-xs whitespace-nowrap ${dark ? 'text-gray-600' : 'text-gray-400'}`}>
                  {formatTime(entry.timestamp)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
